import { useCallback, useEffect, useState } from 'react';
import getNested from 'lodash.get';
import useClientState from './use-client-state';

const useDependencies = (
  dependencies: { key: string; value: any; clickable?: boolean }[]
) => {
  const { state } = useClientState();
  const [isDisabled, setIsDisabled] = useState(false);
  const [isVisuallyDisabled, setIsVisuallyDisabled] = useState(false);

  const checkDependencies = useCallback(async () => {
    if (Array.isArray(dependencies) && dependencies.length > 0) {
      for (const dependency of dependencies) {
        if (dependency.value) {
          if (Array.isArray(dependency.value)) {
            let atLeastOneMatch = false;
            for (const value of dependency.value) {
              if (getNested(state, dependency.key, undefined) === value) {
                atLeastOneMatch = true;
                break;
              }
            }

            if (!atLeastOneMatch) throw dependency;

            continue;
          }

          if (getNested(state, dependency.key, undefined) !== dependency.value)
            throw dependency;

          continue;
        }

        if (!getNested(state, dependency.key, undefined)) throw dependency;
      }
    }
  }, [dependencies, state]);

  useEffect(() => {
    let shouldSetState = true;
    let disabled = false;
    let visuallyDisabled = false;

    checkDependencies()
      .catch((dependency) => {
        if (dependency.clickable) {
          visuallyDisabled = true;
        } else {
          disabled = true;
        }
      })
      .then(() => {
        if (shouldSetState) {
          setIsDisabled(disabled);
          setIsVisuallyDisabled(visuallyDisabled);
        }
      });

    // Don't set state after component has been unmounted
    return () => {
      shouldSetState = false;
    };
  }, [checkDependencies]);

  return { checkDependencies, isDisabled, isVisuallyDisabled };
};

export default useDependencies;
